const BARE_URL_RE = /(?:^|[\s(])((?:https?:\/\/)[^\s<>"')\]]+)/gi;
const MARKDOWN_URL_RE = /\[[^\]]*\]\((https?:\/\/[^\s)]+)\)/gi;
const HTML_HREF_RE = /\bhref\s*=\s*["'](https?:\/\/[^"']+)["']/gi;

function escapeHtmlAttr(text) {
  return String(text || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

function trimUrlTrailingPunctuation(url) {
  let value = String(url || '').trim();
  while (value.length && /[.,;:!?)}\]'"]$/.test(value)) {
    value = value.slice(0, -1);
  }
  return value;
}

function normalizeExtractedUrl(url) {
  const trimmed = trimUrlTrailingPunctuation(url);
  if (!trimmed) return '';
  try {
    const parsed = new URL(trimmed);
    if (!['http:', 'https:'].includes(parsed.protocol)) return '';
    return parsed.href;
  } catch {
    return '';
  }
}

/** Compact label for autolinked URLs — avoids a long raw URL dominating the comment line. */
export function commentLinkDisplayLabel(url, { maxLen = 40 } = {}) {
  const normalized = normalizeExtractedUrl(url);
  const raw = normalized || String(url || '').trim();
  if (!raw) return '';
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./i, '');
    const path = `${parsed.pathname || ''}${parsed.search || ''}${parsed.hash || ''}`;
    const compact = !path || path === '/' ? host : `${host}${path}`;
    if (compact.length <= maxLen) return compact;
    return `${compact.slice(0, Math.max(8, maxLen - 1))}…`;
  } catch {
    if (raw.length <= maxLen) return raw;
    return `${raw.slice(0, Math.max(8, maxLen - 1))}…`;
  }
}

export function normalizeCommentLinkUrl(url) {
  return normalizeExtractedUrl(url);
}

function addUrl(urls, raw) {
  const normalized = normalizeExtractedUrl(raw);
  if (normalized) urls.add(normalized);
}

/** Collect unique http(s) URLs from stored comment bodies (markdown or HTML). */
export function extractUrlsFromCommentBody(body, { max = 2 } = {}) {
  const raw = String(body || '');
  if (!raw.trim()) return [];
  const urls = new Set();

  let match;
  const hrefRe = new RegExp(HTML_HREF_RE.source, HTML_HREF_RE.flags);
  while ((match = hrefRe.exec(raw)) !== null) {
    if (!/^mailto:/i.test(match[1])) addUrl(urls, match[1]);
  }

  const mdRe = new RegExp(MARKDOWN_URL_RE.source, MARKDOWN_URL_RE.flags);
  while ((match = mdRe.exec(raw)) !== null) {
    addUrl(urls, match[1]);
  }

  const bareRe = new RegExp(BARE_URL_RE.source, BARE_URL_RE.flags);
  while ((match = bareRe.exec(raw)) !== null) {
    addUrl(urls, match[1]);
  }

  return [...urls].slice(0, Math.max(1, max));
}

/** Turn bare http(s) URLs in display HTML into clickable links (skips existing tags). */
export function linkifyBareUrlsInDisplayHtml(html) {
  const parts = String(html || '').split(/(<[^>]+>)/g);
  const linked = parts.map((part) => {
    if (!part || part.startsWith('<')) return part;
    return part.replace(BARE_URL_RE, (full, url) => {
      const normalized = normalizeExtractedUrl(url);
      if (!normalized) return full;
      const prefix = full.slice(0, full.indexOf(url));
      const suffix = full.slice(prefix.length + url.length);
      const displayUrl = trimUrlTrailingPunctuation(url);
      const trailing = url.slice(displayUrl.length);
      const safeHref = normalized.replace(/"/g, '&quot;');
      const label = escapeHtmlAttr(commentLinkDisplayLabel(displayUrl));
      return `${prefix}<a href="${safeHref}" class="support-comment-link" data-comment-link="1">${label}</a>${trailing}${suffix}`;
    });
  });
  return linked.join('');
}

export function linkPreviewHostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return String(url || '');
  }
}

export function getCommentLinkDestination(url) {
  const normalized = normalizeCommentLinkUrl(url);
  if (!normalized) return null;
  try {
    const parsed = new URL(normalized);
    return {
      fullUrl: normalized,
      host: parsed.hostname.replace(/^www\./i, ''),
      rawHost: parsed.hostname,
      port: parsed.port && !['80', '443'].includes(parsed.port) ? parsed.port : '',
      protocol: parsed.protocol,
    };
  } catch {
    return null;
  }
}

function normalizeHostnameForCompare(hostname) {
  return String(hostname || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
}

function hostsAreRelated(actualHost, claimedHost) {
  const actual = normalizeHostnameForCompare(actualHost);
  const claimed = normalizeHostnameForCompare(claimedHost);
  if (!actual || !claimed) return true;
  if (actual === claimed) return true;
  return actual.endsWith(`.${claimed}`) || claimed.endsWith(`.${actual}`);
}

function mergeTrustWarnings(...groups) {
  const merged = [];
  const seen = new Set();
  for (const group of groups) {
    for (const warning of group || []) {
      const code = String(warning?.code || '').trim();
      if (!code || seen.has(code)) continue;
      seen.add(code);
      merged.push({
        code,
        message: String(warning?.message || '').trim(),
      });
    }
  }
  return merged.filter((warning) => warning.message);
}

/** Client-side trust checks for comment links (merged with server preview warnings when present). */
export function getCommentLinkTrustWarnings(url, preview = null) {
  const dest = getCommentLinkDestination(url);
  if (!dest) return [];

  const local = [];
  if (/^xn--/i.test(dest.rawHost) || /\.xn--/i.test(dest.rawHost)) {
    local.push({
      code: 'punycode_host',
      message:
        'This link uses an internationalized domain (punycode). Double-check the address before continuing.',
    });
  }
  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(dest.rawHost) || (dest.rawHost.includes(':') && !dest.rawHost.startsWith('['))) {
    local.push({
      code: 'ip_host',
      message: 'This link goes to a numeric IP address instead of a familiar website name.',
    });
  }
  if (dest.port) {
    local.push({
      code: 'non_default_port',
      message: `This link uses port ${dest.port}, which is unusual for everyday browsing.`,
    });
  }

  const declaredSiteName = preview?.declaredSiteName || preview?.siteName || '';
  if (declaredSiteName && !hostsAreRelated(dest.host, declaredSiteName)) {
    local.push({
      code: 'site_name_mismatch',
      message: `The page claims to be from “${declaredSiteName}”, but the link goes to ${dest.host}.`,
    });
  }

  return mergeTrustWarnings(local, preview?.trustWarnings);
}

export function commentLinkHasTrustWarnings(url, preview = null) {
  return getCommentLinkTrustWarnings(url, preview).length > 0;
}
